import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { ASYNC_DELETE_FUND, ASYNC_RESTORE_FUND } from 'store';
import { Row, Col, Card, Button } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from 'services';
import Dialog from 'react-bootstrap-dialog';
import MasterPage from 'components/MasterPage';
import Listing from 'components/Listing';
import Can from 'components/Can';
import { DashboardOutlined } from '@material-ui/icons';

const FundsList = ({ deleteFund, restoreFund, trash }) => {
  const defaultFilters = {
    search: '',
    per_page: 10,
    page: 1,
    fiscal_year: '',
  };
  const [data, setData] = useState({ data: [] });
  const [filters, setFilters] = useState(defaultFilters);

  let dialog;

  const { t } = useTranslation();

  useEffect(() => {
    api
      .get(!trash ? 'funds' : 'funds/trash', { params: { ...filters } })
      .then(({ data }) => setData(data));
  }, [filters, trash]);

  const effectDeleteFund = (id) => {
    Dialog.setOptions({
      defaultOkLabel: t('label:yes'),
      defaultCancelLabel: t('label:no'),
      primaryClassName: 'btn-success',
      defaultButtonClassName: 'btn-danger',
    });

    dialog.show({
      title: t('title:fund delete'),
      body: t('phrase:are you sure?'),
      actions: [Dialog.CancelAction(), Dialog.OKAction(() => deleteFund(id))],
      bgSize: 'small',
    });
  };

  const effectRestoreFund = (id) => {
    Dialog.setOptions({
      defaultOkLabel: t('label:yes'),
      defaultCancelLabel: t('label:no'),
      primaryClassName: 'btn-success',
      defaultButtonClassName: 'btn-danger',
    });

    dialog.show({
      title: t('title:fund restore'),
      body: t('phrase:are you sure?'),
      actions: [Dialog.CancelAction(), Dialog.OKAction(() => restoreFund(id))],
      bgSize: 'small',
    });
  };

  const handleFilterChange = (new_filters, reset = false) => {
    let f = { ...filters, ...new_filters };

    if (reset) f = { ...defaultFilters, ...new_filters };

    setFilters(f);
  };

  return (
    <>
      <Card.Body className="shadow bg-white rounded mb-3">
        <Dialog
          ref={(component) => {
            dialog = component;
          }}
        />
        <Row>
          <Col className="d-flex mt-3 mb-3">
            <DashboardOutlined className="icon-title" />
            <h4 className="title-dashboard mt-2">
              {!!trash ? t('title:trash') + ': ' : ''}
              {t('title:funds list')}
            </h4>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col>
            <Row className="mb-5">
              <Col>
                {!trash ? (
                  <>
                    <Can do="createFund" on="App">
                      <Button
                        size="sm"
                        className="login-btn mr-1"
                        variant="primary-login"
                        as={Link}
                        to="/funds/create"
                      >
                        <i className="fas fa-plus"></i> {t('label:add new')}
                      </Button>
                    </Can>
                    &nbsp;
                    <Can do="listUsers" on="App">
                      <Button
                        size="sm"
                        variant="secondary"
                        as={Link}
                        to="/funds/trash"
                      >
                        <i className="fas fa-trash"></i> {t('label:view trash')}
                      </Button>
                    </Can>
                  </>
                ) : (
                    <Button size="sm" className="login-btn mr-1" variant="primary-login" as={Link} to="/funds">
                      <i className="fas fa-arrow-left"></i> {t('label:go back')}
                    </Button>
                  )}
              </Col>
            </Row>
            <Row className="mt-2">
              <Col>
                <Listing
                  fields={[
                    { name: 'company', key: 'company_alias', type: 'string' },
                    {
                      name: 'fund type',
                      key: 'type_name',
                      type: 'string',
                      trans: 'label',
                    },
                    {
                      name: 'period',
                      type: 'custom',
                      value: (_, dt) =>
                        dt.company_rule_id 
                          ? `${dt.period_type_name} ${dt.period_position}`
                          : '--',
                    },
                    {
                      name: 'Cotação',
                      key: 'value_cotacao',
                      type: 'currency'
                    },
                    {
                      name: 'expected value',
                      key: 'value_expected',
                      type: 'currency mask',
                    },
                    {
                      name: 'expected value in real currency',
                      key: 'value_expected_real',
                      type: 'currency',
                    },
                    {
                      name: 'real value',
                      key: 'value_real',
                      type: 'currency mask',
                    },
                    {
                      name: 'real value in real currency',
                      key: 'value_currency_real',
                      type: 'currency',
                    },
                  ]}
                  filters={[]}
                  stateFilters={filters}
                  setFilters={setFilters}
                  filterFiscalYears
                  data={data}
                  handleFilterChange={handleFilterChange}
                  actions={
                    !trash
                      ? [
                          (fund, k) => (
                            <Can do="editFund" on="App">
                              <Button
                                size="sm"
                                key={k}
                                as={Link}
                                to={`/funds/edit/${fund.id}`}
                                variant="light"
                                alt={t('title:edit')}
                                title={t('title:edit')}
                              >
                                <i className="fas fa-pen"></i>
                              </Button>
                            </Can>
                          ),
                          (fund, k) => (
                            <Can do="deleteFund" on="App">
                              <Button
                                size="sm"
                                key={k}
                                className="ml-1"
                                variant="danger"
                                onClick={() => effectDeleteFund(fund.id)}
                                alt={t('title:delete')}
                                title={fund.inactivate ? t('title:inactivate') : t('title:delete')}
                                disabled={fund.inactivate}
                              >
                                <i className="fas fa-times"></i>
                              </Button>
                            </Can>
                          ),
                        ]
                      : [
                          (fund, k) => (
                            <Can do="restoreUser" on="App">
                              <Button
                                size="sm"
                                key={k}
                                variant="secondary"
                                onClick={() => effectRestoreFund(fund.id)}
                                alt={t('title:restore')}
                                title={t('title:restore')}
                              >
                                <i className="fas fa-trash-restore-alt"></i>
                              </Button>
                            </Can>
                          ),
                        ]
                  }
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </Card.Body>
    </>
  );
};
const mapStateToProps = (state) => ({});

const mapDispatchToProps = (dispatch) => ({
  deleteFund: (id) => dispatch({ type: ASYNC_DELETE_FUND, payload: { id } }),
  restoreFund: (id) => dispatch({ type: ASYNC_RESTORE_FUND, payload: { id } }),
});

const reduxConnect = connect(mapStateToProps, mapDispatchToProps)(FundsList);

export default MasterPage(reduxConnect);
